import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, X, Zap } from 'lucide-react';
import type { Node } from 'reactflow';
import type { AgentKindId, AgentOperation, AgentOperationKind, FlowAgentPersistedData, LlmBackboneId } from './agentWorkflowTypes';
import {
  AGENT_SOFT_OP_LIMIT,
  AGENT_TYPE_META,
  LLM_BACKBONE_OPTIONS,
  buildAgentOutputLines,
  operationsFromAgentKind,
  slugifyAgentNodeName,
} from './agentWorkflowTypes';
import './AgentConfigPanel.css';
import { WorkflowConfigPanelMaximizeButton } from './WorkflowConfigPanelFrame';

type AgentConfigPanelProps = {
  node: Node<FlowAgentPersistedData>;
  panelMaximized?: boolean;
  onTogglePanelMaximize?: () => void;
  onUpdate: (nodeId: string, patch: Partial<FlowAgentPersistedData>) => void;
  onReorderOps: (nodeId: string, from: number, to: number) => void;
  onRemoveOp: (nodeId: string, opId: string) => void;
  onAddOp: (nodeId: string, op: Omit<AgentOperation, 'id'>) => void;
  onDelete: (nodeId: string) => void;
  onClose: () => void;
};

const KIND_OPTIONS: AgentKindId[] = [
  'data-analyst',
  'email-composer',
  'research-agent',
  'report-generator',
  'approval-router',
  'custom',
];

const ADD_OP_KINDS: AgentOperationKind[] = ['auto', 'llm', 'mcp', 'action', 'emit'];

export default function AgentConfigPanel({
  node,
  panelMaximized = false,
  onTogglePanelMaximize,
  onUpdate,
  onReorderOps,
  onRemoveOp,
  onAddOp,
  onDelete,
  onClose,
}: AgentConfigPanelProps) {
  const d = node.data;
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addKind, setAddKind] = useState<AgentOperationKind>('llm');
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [expandedOpId, setExpandedOpId] = useState<string | null>(null);

  const outputs = useMemo(() => buildAgentOutputLines(d.nodeName, d.outputSchemaJson), [d.nodeName, d.outputSchemaJson]);

  const validateSchema = (text: string) => {
    try {
      JSON.parse(text);
      setSchemaError(null);
    } catch {
      setSchemaError('Invalid JSON');
    }
  };

  const patchOp = (opId: string, patch: Partial<AgentOperation>) => {
    onUpdate(node.id, {
      operations: d.operations.map((o) => (o.id === opId ? { ...o, ...patch } : o)),
    });
  };

  const onDragStart = (i: number) => (e: React.DragEvent) => {
    setDragIdx(i);
    e.dataTransfer.setData('application/x-agent-op', String(i));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDropRow = (to: number) => (e: React.DragEvent) => {
    e.preventDefault();
    const fromStr = e.dataTransfer.getData('application/x-agent-op');
    const from = parseInt(fromStr, 10);
    if (Number.isNaN(from) || from === to) return;
    onReorderOps(node.id, from, to);
    setDragIdx(null);
  };

  const submitAddOp = () => {
    const name = addName.trim() || 'New operation';
    const base: Omit<AgentOperation, 'id'> = {
      name,
      kind: addKind,
    };
    if (addKind === 'emit') {
      base.eventName = 'custom.event';
      base.eventPayload = '{}';
    }
    if (d.operations.length >= AGENT_SOFT_OP_LIMIT) {
      window.alert(`Soft limit: at most ${AGENT_SOFT_OP_LIMIT} operations.`);
      return;
    }
    onAddOp(node.id, base);
    setAddOpen(false);
    setAddName('');
    setAddKind('llm');
  };

  const testAgent = () => {
    onUpdate(node.id, { runStatus: 'running' });
    window.setTimeout(() => {
      onUpdate(node.id, { runStatus: 'success' });
    }, 1400);
  };

  const expanded = d.operations.find((o) => o.id === expandedOpId);

  return (
    <aside className="workflow-agent-panel" aria-label="Agent configuration">
      <div className="workflow-trigger-panel__head">
        <h2 className="workflow-agent-panel__title">Agent</h2>
        <div className="workflow-trigger-panel__head-actions">
          {onTogglePanelMaximize ? (
            <WorkflowConfigPanelMaximizeButton expanded={panelMaximized} onToggle={onTogglePanelMaximize} />
          ) : null}
          <button type="button" className="workflow-trigger-panel__close" onClick={onClose} aria-label="Close panel">
            <X size={18} strokeWidth={2} />
          </button>
        </div>
      </div>
      <p className="workflow-agent-panel__subtitle">{d.label}</p>

      <div className="workflow-trigger-panel__section">
        <label className="workflow-trigger-panel__label" htmlFor={`agent-nodename-${node.id}`}>
          Node name
        </label>
        <input
          id={`agent-nodename-${node.id}`}
          className="workflow-trigger-panel__input"
          value={d.nodeName}
          onChange={(e) => onUpdate(node.id, { nodeName: slugifyAgentNodeName(e.target.value) })}
        />
      </div>

      <div className="workflow-trigger-panel__section">
        <label className="workflow-trigger-panel__label" htmlFor={`agent-kind-${node.id}`}>
          Agent type
        </label>
        <select
          id={`agent-kind-${node.id}`}
          className="workflow-trigger-panel__select"
          value={d.agentKind}
          onChange={(e) => {
            const k = e.target.value as AgentKindId;
            const meta = AGENT_TYPE_META[k];
            onUpdate(node.id, {
              agentKind: k,
              label: meta.title,
              objective: meta.defaultObjective,
              outputSchemaJson: meta.defaultSchema,
              nodeName: slugifyAgentNodeName(meta.title),
              operations: operationsFromAgentKind(k),
            });
          }}
        >
          {KIND_OPTIONS.map((k) => (
            <option key={k} value={k}>
              {AGENT_TYPE_META[k].title}
            </option>
          ))}
        </select>
      </div>

      <div className="workflow-trigger-panel__section">
        <span className="workflow-trigger-panel__label">LLM backbone</span>
        <div className="workflow-agent-panel__backbone">
          {LLM_BACKBONE_OPTIONS.map((b) => (
            <button
              key={b.id}
              type="button"
              className={`workflow-agent-panel__backbone-card${d.backbone === b.id ? ' workflow-agent-panel__backbone-card--on' : ''}`}
              onClick={() => onUpdate(node.id, { backbone: b.id as LlmBackboneId })}
            >
              <span className="workflow-agent-panel__backbone-dot">{d.backbone === b.id ? '●' : '○'}</span>
              <span className="workflow-agent-panel__backbone-label">{b.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="workflow-trigger-panel__section">
        <label className="workflow-trigger-panel__label" htmlFor={`agent-obj-${node.id}`}>
          Objective
        </label>
        <textarea
          id={`agent-obj-${node.id}`}
          className="workflow-llm-panel__textarea"
          rows={5}
          value={d.objective}
          onChange={(e) => onUpdate(node.id, { objective: e.target.value })}
        />
      </div>

      <div className="workflow-trigger-panel__section">
        <label className="workflow-trigger-panel__label" htmlFor={`agent-inmap-${node.id}`}>
          Input mapping
        </label>
        <input
          id={`agent-inmap-${node.id}`}
          className="workflow-trigger-panel__input"
          value={d.inputMappingOverride ?? ''}
          onChange={(e) =>
            onUpdate(node.id, {
              inputMappingOverride: e.target.value.trim() === '' ? null : e.target.value,
            })
          }
          placeholder="{{ previous.output }}"
        />
        <p className="workflow-agent-panel__hint">Leave empty to use the connection hint on the node.</p>
      </div>

      <div className="workflow-trigger-panel__section">
        <span className="workflow-trigger-panel__label">Operations</span>
        {d.operations.length >= AGENT_SOFT_OP_LIMIT && (
          <p className="workflow-llm-panel__warn">Max {AGENT_SOFT_OP_LIMIT} operations (soft limit).</p>
        )}
        <ul className="workflow-agent-panel__op-list">
          {d.operations.map((op, i) => (
            <li
              key={op.id}
              className={`workflow-agent-panel__op-item${dragIdx === i ? ' workflow-agent-panel__op-item--drag' : ''}${
                expandedOpId === op.id ? ' workflow-agent-panel__op-item--open' : ''
              }`}
              draggable
              onDragStart={onDragStart(i)}
              onDragEnd={() => setDragIdx(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDropRow(i)}
            >
              <span className="workflow-agent-panel__op-grip">≡</span>
              <button type="button" className="workflow-agent-panel__op-main" onClick={() => setExpandedOpId((x) => (x === op.id ? null : op.id))}>
                <span className="workflow-agent-panel__op-idx">{i + 1}</span>
                <span className="workflow-agent-panel__op-name">{op.name}</span>
                {op.kind === 'emit' && <Zap size={12} className="workflow-agent-panel__op-zap" strokeWidth={2} aria-hidden />}
              </button>
              <button type="button" className="workflow-agent-panel__op-x" aria-label="Remove" onClick={() => onRemoveOp(node.id, op.id)}>
                ×
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="workflow-agent-panel__add-btn" onClick={() => setAddOpen(true)} disabled={d.operations.length >= AGENT_SOFT_OP_LIMIT}>
          + Add operation
        </button>
      </div>

      {expanded && expanded.kind === 'emit' && (
        <div className="workflow-trigger-panel__section workflow-agent-panel__emit-box">
          <span className="workflow-trigger-panel__label">Event (operation)</span>
          <label className="workflow-trigger-panel__label">Event name</label>
          <input
            className="workflow-trigger-panel__input"
            value={expanded.eventName ?? ''}
            onChange={(e) => patchOp(expanded.id, { eventName: e.target.value })}
          />
          <label className="workflow-trigger-panel__label">Payload template</label>
          <textarea
            className="workflow-llm-panel__textarea workflow-llm-panel__textarea--mono"
            rows={4}
            value={expanded.eventPayload ?? '{}'}
            onChange={(e) => patchOp(expanded.id, { eventPayload: e.target.value })}
          />
        </div>
      )}

      <div className="workflow-trigger-panel__section">
        <button
          type="button"
          className="workflow-agent-panel__collapse"
          onClick={() => onUpdate(node.id, { eventSectionCollapsed: !d.eventSectionCollapsed })}
        >
          {d.eventSectionCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          Event emission (global notes)
        </button>
        {!d.eventSectionCollapsed && (
          <p className="workflow-agent-panel__hint">
            Configure per-operation emit steps above. Use emit operations in the list for <code>lead.scored</code>-style events.
          </p>
        )}
      </div>

      <div className="workflow-trigger-panel__section">
        <label className="workflow-trigger-panel__label" htmlFor={`agent-schema-${node.id}`}>
          Output schema (JSON)
        </label>
        <textarea
          id={`agent-schema-${node.id}`}
          className="workflow-llm-panel__textarea workflow-llm-panel__textarea--mono"
          rows={8}
          value={d.outputSchemaJson}
          onChange={(e) => {
            onUpdate(node.id, { outputSchemaJson: e.target.value });
            validateSchema(e.target.value);
          }}
        />
        {schemaError && <p className="workflow-agent-panel__schema-err">{schemaError}</p>}
      </div>

      <div className="workflow-trigger-panel__section">
        <span className="workflow-trigger-panel__label">Available outputs</span>
        <pre className="workflow-trigger-panel__outputs workflow-agent-panel__outputs">{outputs}</pre>
      </div>

      <div className="workflow-agent-panel__footer-actions">
        <button type="button" className="workflow-agent-panel__test" onClick={testAgent}>
          Test agent
        </button>
        <button type="button" className="workflow-trigger-panel__btn workflow-trigger-panel__btn--danger" onClick={() => onDelete(node.id)}>
          Delete
        </button>
      </div>

      {addOpen && (
        <div className="agent-add-op__backdrop" role="dialog" aria-modal="true" onClick={() => setAddOpen(false)}>
          <div className="agent-add-op" onClick={(e) => e.stopPropagation()}>
            <h3 className="agent-add-op__title">Add operation</h3>
            <label className="workflow-trigger-panel__label">Name</label>
            <input className="workflow-trigger-panel__input" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Validate data" />
            <span className="workflow-trigger-panel__label">Type</span>
            <div className="agent-add-op__types">
              {ADD_OP_KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`agent-add-op__type${addKind === k ? ' agent-add-op__type--on' : ''}`}
                  onClick={() => setAddKind(k)}
                >
                  {k === 'llm' ? 'LLM' : k === 'mcp' ? 'MCP' : k === 'emit' ? 'Event' : k.charAt(0).toUpperCase() + k.slice(1)}
                </button>
              ))}
            </div>
            <div className="agent-add-op__actions">
              <button type="button" className="agent-add-op__cancel" onClick={() => setAddOpen(false)}>
                Cancel
              </button>
              <button type="button" className="agent-add-op__submit" onClick={submitAddOp}>
                Add operation
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
